'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ArrowRight, ArrowUpRight, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import type { CommonContent } from '@/lib/common-content'
import { COURSES, coursePath } from '@/data/catalog'
import { Skyline } from './Skyline'
import { LiveClocks } from './LiveClocks'
import { FlagJP, FlagPK } from './Flags'
import { ApplyButton } from '@/components/apply/ApplyButton'

const WHATSAPP = 'https://wa.me/923001301223'
const SOCIALS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/p/Nippon-Center-Pakistan-61573966641213/',
    path: 'M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.8c0-.5.3-.8.5-.8z',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/nippon-center-pk/',
    path: 'M4.5 8.5h3.6V20H4.5zM6.3 3a2.1 2.1 0 110 4.2 2.1 2.1 0 010-4.2zM10.3 8.5h3.4v1.6h.1c.5-.9 1.7-1.9 3.5-1.9 3.7 0 4.4 2.4 4.4 5.6V20h-3.6v-5.4c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V20h-3.6z',
  },
]

const FEATURED = ['japanese-foundations', 'jlpt-n5', 'jlpt-n4', 'jft-basic', 'ssw-preparation', 'caregiving-japanese', 'business-japanese', 'japanese-for-kids']

const L = {
  en: {
    big: 'From K2 to Fuji.',
    sub: 'Japanese language, JLPT and Japan careers, taught in Lahore and Alipur Chatha. 2026/27 intake now open.',
    apply: 'Apply for 2026/27',
    whatsapp: 'WhatsApp us',
    call: 'Call',
    courses: 'Popular courses',
    all: 'All courses',
    explore: 'Explore',
    explorer: [
      ['/work-in-japan', 'Work in Japan (SSW & TITP)'],
      ['/jlpt', 'JLPT N5–N1'],
      ['/individuals', 'For individuals'],
      ['/kids', 'For kids'],
      ['/business', 'For business'],
      ['/about', 'About us'],
      ['/careers', 'Careers'],
      ['/contact', 'Contact'],
    ],
    campuses: 'Campuses',
    directions: 'Directions',
    contact: 'Talk to us',
    follow: 'Follow us',
  },
  ja: {
    big: 'K2から富士山へ。',
    sub: '日本語、JLPT、日本での就職を、ラホールとアリプル・チャタで。2026/27年度 受付中。',
    apply: '2026/27年度に申し込む',
    whatsapp: 'WhatsAppで相談',
    call: '電話',
    courses: '人気のコース',
    all: 'すべてのコース',
    explore: 'サイトマップ',
    explorer: [
      ['/work-in-japan', '日本で働く（特定技能・技能実習）'],
      ['/jlpt', 'JLPT N5〜N1'],
      ['/individuals', '個人の方'],
      ['/kids', 'キッズ'],
      ['/business', '法人の方'],
      ['/about', '私たちについて'],
      ['/careers', '採用情報'],
      ['/contact', 'お問い合わせ'],
    ],
    campuses: 'キャンパス',
    directions: '地図',
    contact: 'お問い合わせ',
    follow: 'フォローする',
  },
}

export function SiteFooter({ contentEn, contentJa }: { contentEn: CommonContent; contentJa: CommonContent }) {
  const pathname = usePathname() ?? '/en'
  const locale: 'en' | 'ja' = pathname.startsWith('/ja') ? 'ja' : 'en'
  const c = locale === 'ja' ? contentJa : contentEn
  const t = L[locale]
  const p = (h: string) => `/${locale}${h}`
  const tel = `tel:${(c.footerPhone ?? '').replace(/[^\d+]/g, '')}`
  const parse = (text: string) =>
    (text ?? '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const m = line.match(/^([^:：]+)[:：](.*)$/)
        return m ? { name: m[1].trim(), address: m[2].trim() } : { name: '', address: line }
      })
  const campuses = parse(c.footerAddress)
  // map links always use the English address (Google Maps finds it reliably)
  const mapQuery = parse(contentEn.footerAddress)
  const year = new Date().getFullYear()

  return (
    <footer className="relative isolate mt-10 text-white/75">
      <Skyline className="relative z-[1] -mb-px block h-[96px] w-full sm:h-[130px] md:h-[170px]" />
      <div className="relative overflow-hidden bg-brand-navy">
        <div className="ncp-asanoha pointer-events-none absolute inset-0 -z-10" aria-hidden />
        <div className="pointer-events-none absolute -top-40 left-[10%] -z-10 h-80 w-80 rounded-full bg-brand-green/40 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -top-32 right-[8%] -z-10 h-72 w-72 rounded-full bg-brand-red/25 blur-3xl" aria-hidden />

        {/* statement + CTAs */}
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-8 lg:px-6">
          <div className="grid items-end gap-6 border-b border-white/10 pb-10 xl:grid-cols-[1.3fr_1fr]">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-gold">
                <FlagPK /> <span className="h-px w-6 bg-brand-gold/60" /> <FlagJP />
              </p>
              <h2 className="mt-4 text-4xl font-bold tracking-[-0.03em] text-white sm:text-5xl md:text-6xl">{t.big}</h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-white/70">{t.sub}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-nowrap sm:items-center xl:justify-end">
              <ApplyButton className="btn-primary col-span-2 h-12 whitespace-nowrap sm:col-span-1">
                {t.apply} <ArrowRight className="ml-2 h-4 w-4" />
              </ApplyButton>
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-magnetic
                className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#25D366] px-5 text-sm font-semibold text-white shadow-[0_10px_28px_-12px_rgba(37,211,102,0.8)] transition-colors hover:bg-[#1ebe5b] md:text-base"
              >
                <MessageCircle className="h-5 w-5 shrink-0" /> {t.whatsapp}
              </a>
              <a
                href={tel}
                data-magnetic
                aria-label={`${t.call} ${c.footerPhone}`}
                className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/25 px-5 text-sm font-semibold text-white transition-colors hover:border-brand-gold hover:text-brand-gold md:text-base"
              >
                <Phone className="h-4 w-4 shrink-0" /> {t.call}
              </a>
            </div>
          </div>

          {/* columns */}
          <div className="grid gap-10 pt-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_1fr_0.9fr_1.3fr]">
            <div>
              <Link href={p('')} className="inline-flex items-center gap-3">
                <span className="rounded-2xl bg-white/95 px-3 py-2 shadow-lg">
                  <Image src="/ncp-mark.webp" alt="" width={346} height={192} className="h-10 w-auto" />
                </span>
                <span className="leading-tight">
                  <span className="block whitespace-nowrap text-base font-bold text-white">Nippon Center Pakistan</span>
                  <span className="block font-[family-name:var(--font-noto-serif-jp)] text-xs tracking-[0.2em] text-brand-gold">日本センター・パキスタン</span>
                </span>
              </Link>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/65">{c.footerBrandDesc}</p>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">{t.follow}</p>
              <div className="mt-3 flex gap-2">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
                      <path d={s.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <nav aria-label={t.courses}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold">{t.courses}</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                {FEATURED.map((slug) => {
                  const course = COURSES.find((x) => x.slug === slug)
                  if (!course) return null
                  return (
                    <li key={slug}>
                      <Link href={coursePath(locale, slug)} className="transition-colors hover:text-white">
                        {course.title[locale]}
                      </Link>
                    </li>
                  )
                })}
                <li>
                  <Link href={p('/courses')} className="inline-flex items-center gap-1 font-semibold text-brand-gold hover:text-white">
                    {t.all} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              </ul>
            </nav>

            <nav aria-label={t.explore}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold">{t.explore}</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                {t.explorer.map(([href, label]) => (
                  <li key={href}>
                    <Link href={p(href)} className="transition-colors hover:text-white">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold">{t.campuses}</p>
              <ul className="mt-4 space-y-4 text-sm">
                {campuses.map((cp, i) => (
                  <li key={cp.address} className="flex gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>
                      {cp.name && <b className="block font-semibold text-white">{cp.name}</b>}
                      <span className="block leading-relaxed">{cp.address}</span>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((mapQuery[i] ?? cp).address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-brand-gold hover:text-white"
                      >
                        {t.directions} <ArrowUpRight className="h-3 w-3" />
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold">{t.contact}</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <a href={tel} className="inline-flex items-center gap-2 hover:text-white">
                    <Phone className="h-4 w-4 text-brand-gold" /> {c.footerPhone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${c.footerEmail}`} className="inline-flex items-center gap-2 hover:text-white">
                    <Mail className="h-4 w-4 text-brand-gold" /> {c.footerEmail}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* bottom bar: copyright left, clocks centre, policies right */}
        <div className="border-t border-white/10">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-3 px-4 py-5 text-center text-xs text-white/55 md:grid-cols-3 md:text-left lg:px-6">
            <p className="md:justify-self-start">
              © {year} {c.footerCopyright}
            </p>
            <LiveClocks locale={locale} className="justify-self-center text-white/60" />
            <div className="flex items-center justify-center gap-4 md:justify-self-end">
              <Link href={p('/privacy-policy')} className="hover:text-white">
                {c.footerPrivacyPolicy}
              </Link>
              <Link href={p('/terms-of-service')} className="hover:text-white">
                {c.footerTermsOfService}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
